import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  auth,
  onAuthStateChanged,
  signOut,
  googleProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
} from '../firebase';
import { cmsApi } from '../api';

const AuthContext = createContext(null);

// Shape the Firebase user into a clean profile object
const shapeUser = (fbUser) => ({
  uid:      fbUser.uid,
  name:     fbUser.displayName || fbUser.email?.split('@')[0] || 'User',
  email:    fbUser.email,
  avatar:   fbUser.photoURL || (fbUser.displayName?.[0] || fbUser.email?.[0] || 'U').toUpperCase(),
  provider: fbUser.providerData?.[0]?.providerId || 'email',
  photo:    fbUser.photoURL || null,
});


export function AuthProvider({ children }) {
  const [user, setUser]       = useState(undefined); // undefined = loading
  const [loading, setLoading] = useState(true);

  // Listen to Firebase auth state — source of truth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        const shaped = shapeUser(fbUser);
        setUser(shaped);
        // Upsert customer record in DB — fire-and-forget, never blocks login
        cmsApi.saveCustomer({
          uid:      shaped.uid,
          name:     shaped.name,
          email:    shaped.email,
          photo:    shaped.photo || null,
          provider: shaped.provider,
        }).catch(() => {/* non-critical */});
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // ── Google sign-in ─────────────────────────────────────────────
  const loginWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    return shapeUser(result.user);
  };

  // ── Email + password sign-in ───────────────────────────────────
  const loginWithEmail = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return shapeUser(result.user);
  };

  // ── Email + password sign-up ───────────────────────────────────
  const signupWithEmail = async (name, email, password) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    // Set display name immediately
    await updateProfile(result.user, { displayName: name });
    // Force refresh so displayName is on the user object
    await result.user.reload();
    return shapeUser(auth.currentUser);
  };

  // ── Password reset ─────────────────────────────────────────────
  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  // ── Sign out ───────────────────────────────────────────────────
  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  // ── Update display name ────────────────────────────────────────────
  const updateDisplayName = async (name) => {
    const fbUser = auth.currentUser;
    if (!fbUser) throw new Error('Not logged in');
    await updateProfile(fbUser, { displayName: name });
    await fbUser.reload();
    const shaped = shapeUser(auth.currentUser);
    setUser(shaped);
    // Keep the DB customer record in sync
    cmsApi.saveCustomer({
      uid:      shaped.uid,
      name:     shaped.name,
      email:    shaped.email,
      photo:    shaped.photo || null,
      provider: shaped.provider,
    }).catch(() => {});
  };

  // ── Get current Firebase ID token (for authenticated API calls) ─
  const getToken = async () => {
    const fbUser = auth.currentUser;
    if (!fbUser) return null;
    return fbUser.getIdToken();
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithGoogle, loginWithEmail, signupWithEmail, resetPassword, logout, getToken, updateDisplayName }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
